import {
  HOUR,
  Logger,
  toWorkflowStages,
  type WorkflowActionData,
  type WorkflowActionId,
  type WorkflowStageData,
  type WorkflowStageId,
  type WorkflowData,
  type WorkflowGetInput,
  type WorkflowGetSuccess,
  type WorkflowId,
} from '@terros/common'
import { useTantalimQuery } from '../query'
import { type GetHookResponse } from '../hooks'
import { workflowKey } from './keys'

const logger = new Logger('useWorkflow')

export const PLACEHOLDER_WORKFLOW_ID: WorkflowId = 'WF.PLACEHOLDER'

export type DecoratedWorkflowData = WorkflowData & {
  stagesMap: Map<WorkflowStageId, WorkflowStageData>
  actionsMap: Map<WorkflowActionId, WorkflowActionData>
  pipelineStages: WorkflowStageData[]
  otherStages: WorkflowStageData[]
}

export function decorateWorkflow(workflow: WorkflowData): DecoratedWorkflowData {
  const { pipelineStages, otherStages } = toWorkflowStages(workflow.stages)
  const stagesMap = workflow.stages.reduce((acc, stage) => {
    acc.set(stage.stageId, stage)
    return acc
  }, new Map<WorkflowStageId, WorkflowStageData>())
  const actionsMap = workflow.actions.reduce((acc, action) => {
    acc.set(action.actionId, action)
    return acc
  }, new Map<WorkflowActionId, WorkflowActionData>())
  return { ...workflow, stagesMap, actionsMap, pipelineStages, otherStages }
}

export function useWorkflow(
  workflowId: WorkflowId = PLACEHOLDER_WORKFLOW_ID,
  enabled = true
): GetHookResponse<DecoratedWorkflowData> {
  logger.verbose('calling useWorkflow', workflowId)
  return useTantalimQuery<WorkflowGetInput, WorkflowGetSuccess, DecoratedWorkflowData>({
    url: '/workflow/get',
    queryKey: workflowKey(workflowId),
    input: {
      workflowId,
    },
    convertResponse: (data) => decorateWorkflow(data.workflow),
    options: {
      gcTime: HOUR,
      staleTime: HOUR,
      enabled: enabled && workflowId !== PLACEHOLDER_WORKFLOW_ID,
    },
  })
}
